// #region imports

// --- Angular
import { HttpClient, HttpErrorResponse } from "@angular/common/http";

// --- RxJs
import { Observable, catchError, map, of } from "rxjs";

// --- 3rd Party
import { JL } from 'jsnlog';
import { TranslateService } from "@ngx-translate/core";
import { retryBackoff } from "backoff-rxjs";

// --- HS Code
import { environment } from "../../../../environments/environment";
import { ApplicationError, CommandResult, GroupModel, RespondentModel } from "../../../models";
import { AppService } from "../app.service";
import { RemoteCommandOfT } from "./remote-command";
import { CommandTypes } from "../../../models/shared/command-types";
import { CommandHandlerResult } from "../system/command-handler";

// #endregion

export class DeleteRespondentCommand extends RemoteCommandOfT<GroupModel[]> {
  protected readonly _apiUri = `${environment.apiConfig.surveyApiUrl}${environment.apiConfig.participantsGroupsApi}/executecommands`

  protected _respondent: RespondentModel;
  protected _groups: GroupModel[];

  constructor(
    protected _logger: JL.JSNLog,
    groups: GroupModel[],
    respondent: RespondentModel,
    _appService: AppService,
    _httpClient: HttpClient,
    _translateService: TranslateService
  ) {
    super(_appService, _httpClient, _translateService);
    this._groups = groups;
    this._respondent = respondent;
  }

  override execute(): void {
    if (this._executionStatus !== this._COMMAND_STATE_NOT_EXECUTED) return;

    this._executionStatus = this._COMMAND_STATE_EXECUTING;

    const theGroup = this._groups.find((group: GroupModel) => group.id.toLowerCase() === this._respondent.groupId.toLowerCase());
    if (!theGroup) {
      this._executionStatus = this._COMMAND_STATE_NOT_EXECUTED;
      return;
    }

    const respondentsToReIndex = theGroup.respondents.filter((r: RespondentModel) => r.groupIndex > this._respondent.groupIndex && r.isSet);
    const dtos: any[] = [];
    dtos.push({
      commandId: ++DeleteRespondentCommand._commandId,
      identifier: this._respondent.id,
      commandType: CommandTypes.delete,
      target: this._respondent
    });
    respondentsToReIndex.forEach(r => dtos.push({
      commandId: ++DeleteRespondentCommand._commandId,
      identifier: r.id,
      commandType: CommandTypes.update,
      target: r,
      propertyNames: ['groupIndex'],
      newValues: [r.groupIndex - 1]
    }));

    this._appService.setLoading(true);

    this._httpClient.post<CommandResult[]>(this._apiUri, dtos).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._error.bind(this))
      ).subscribe((results: CommandResult[] | CommandHandlerResult) => {

        this._appService.setLoading(false);

        if (Array.isArray(results)) {
          const isSuccess = results.every(r => r.isSuccessful);
          const errorMessage = isSuccess ? '' : this._translateService.instant('v2GeneralCommandErrorMessage');
          if (!isSuccess) {
            this._commandResult$.next([]);
            this._executionStatus = this._COMMAND_STATE_ERROR;
            this._appService.setLastError({ errorMessage: errorMessage } as ApplicationError);
            return;
          }
        }

        const updatedGroup = this._removeRespondentFromSourceGroup(this._groups.find(g => g.id.toLowerCase() === this._respondent.groupId.toLowerCase())!);

        const groupIndex = this._groups.findIndex(g => g.id.toLowerCase() === updatedGroup.id.toLowerCase());
        if (groupIndex >= 0) {
          this._groups.splice(groupIndex, 1, updatedGroup);
        }

        this._commandResult$.next(this._groups);

        this._executionStatus = this._COMMAND_STATE_EXECUTED;
      });
  }

  override undo(): void {
    if (this._executionStatus !== this._COMMAND_STATE_EXECUTED) return;

    this._executionStatus = this._COMMAND_STATE_EXECUTING;

    const theGroup = this._groups.find((group: GroupModel) => group.id.toLowerCase() === this._respondent.groupId.toLowerCase());
    if (!theGroup) {
      this._executionStatus = this._COMMAND_STATE_EXECUTED;
      return;
    }

    const respondentsToReIndex = theGroup.respondents.filter((r: RespondentModel) => r.groupIndex >= this._respondent.groupIndex && r.isSet);

    const dtos: any[] = [];
    respondentsToReIndex.forEach(r => dtos.push({
      commandId: ++DeleteRespondentCommand._commandId,
      identifier: r.id,
      commandType: CommandTypes.update,
      target: r,
      propertyNames: ['groupIndex'],
      newValues: [r.groupIndex + 1]
    }));
    const addCommandId = ++DeleteRespondentCommand._commandId;
    dtos.push({
      commandId: addCommandId,
      identifier: this._respondent.id,
      commandType: CommandTypes.add,
      target: this._respondent
    });

    this._appService.setLoading(true);

    this._httpClient.post<CommandResult[]>(this._apiUri, dtos).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._error.bind(this))
      ).subscribe((results: CommandResult[] | CommandHandlerResult) => {

        this._appService.setLoading(false);

        if (Array.isArray(results)) {
          const isSuccess = results.every(r => r.isSuccessful);
          const errorMessage = isSuccess ? '' : this._translateService.instant('v2GeneralCommandErrorMessage');
          if (!isSuccess) {
            this._commandResult$.next([]);
            this._executionStatus = this._COMMAND_STATE_ERROR;
            this._appService.setLastError({ errorMessage: errorMessage } as ApplicationError);
            return;
          }

          const addResult = results.find(r => r.commandId === addCommandId);
          if (addResult) this._respondent.id = addResult.identifier;
        }

        theGroup.respondents.pop(); // remove the last respondent from the list
        // increment the GroupRespondentIndex for all the respondents after the restored on to make room
        theGroup.respondents.forEach(r => {
          if (r.groupIndex >= this._respondent.groupIndex) {
            r.groupIndex++;
          }
        });
        // insert the restored respondent
        theGroup.respondents.splice(this._respondent.groupIndex - 1, 0, this._respondent);
        // place the group back in the groups array
        const groupIndex = this._groups.findIndex(g => g.id.toLowerCase() === theGroup.id.toLowerCase());
        this._groups.splice(groupIndex, 1, theGroup);
        this._commandResult$.next(this._groups);
        this._executionStatus = this._COMMAND_STATE_NOT_EXECUTED;
      });
  }

  protected _removeRespondentFromSourceGroup(group: GroupModel): GroupModel {
    const theGroup = structuredClone(group);
    if (!theGroup) throw new Error('This should be an impossible situation - consider this an assertion that the group will never be null');

    const deleteRespondentIndex = theGroup.respondents.findIndex(r => r.id?.toLowerCase() === this._respondent.id?.toLowerCase());
    theGroup.respondents.splice(deleteRespondentIndex, 1);

    let maxRespondentIndex = 1;
    theGroup.respondents.forEach(r => {
      if (r.groupIndex > this._respondent.groupIndex) {
        r.groupIndex--;
      }
      maxRespondentIndex = Math.max(maxRespondentIndex, r.groupIndex);
    });

    const emptyCount = theGroup.numberOfRespondents - theGroup.respondents.length;
    for (let i = 0; i < emptyCount; i++) {
      theGroup.respondents.push(this._createEmptyRespondentModel(theGroup.id, maxRespondentIndex + i + 1))
    }

    return theGroup;
  }

  protected _error(err: HttpErrorResponse): Observable<CommandHandlerResult> {
    const correlationId = err.headers.get('correlationId');
    this._logger().error({ errorMessage: 'An error occurred attempting to run a series of participant group commands.', error: err, correlationId: correlationId});
    const errorMessage: string = this._translateService.instant('v2GeneralCommandErrorMessage');
    return of({ isSuccess: false, errorMessage } as CommandHandlerResult)
  }

  private _createEmptyRespondentModel(
    groupId: string,
    groupIndex: number,
    isExpanded = false
  ) {
    return {
      emailAddress: '',
      firstName: '',
      lastName: '',
      groupId,
      groupIndex,
      isExpanded,
      languageId: 0,
    } as RespondentModel;
  }
}
